import { useState } from "react";
import { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { CourseForm } from "../types/forms/course-form";
import { AddressSuggestion } from "../types/address-suggestion";
import {
  TypeAddress,
  TypeAddressLabel,
} from "../types/records/type-address-record";
import { useAdressesFavorites } from "./use-adresses-favorites";

type UseAddressPickerParams = {
  idUtilisateur: number | undefined;
  watch: UseFormWatch<CourseForm>;
  setValue: UseFormSetValue<CourseForm>;
};

/**
 * Recherche d'adresse d'un arrêt : palette de recherche et tiroir
 * d'enregistrement en favori, tous deux rattachés à l'arrêt en cours.
 */
export const useAddressPicker = ({
  idUtilisateur,
  watch,
  setValue,
}: UseAddressPickerParams) => {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteIndex, setPaletteIndex] = useState<number | null>(null);

  const [favoriteDrawerOpen, setFavoriteDrawerOpen] = useState(false);
  const [favoriteDrawerIndex, setFavoriteDrawerIndex] = useState<number | null>(
    null,
  );

  const { favorites, isLoading, isSaving, saveFavorite } =
    useAdressesFavorites(idUtilisateur);

  const openAddressSearch = (index: number) => {
    setPaletteIndex(index);
    setPaletteOpen(true);
  };

  const closeAddressSearch = () => {
    setPaletteOpen(false);
    setPaletteIndex(null);
  };

  const selectAddress = (address: AddressSuggestion) => {
    if (paletteIndex === null) return;

    // Le nom du lieu peut venir de l'adresse (POI/favori API) ou, à défaut, du
    // libellé affiché pour les POI/favoris locaux qui n'ont pas de `nom` dans
    // leur adresse.
    const nom =
      address.adresse.nom ??
      (address.type === "poi" || address.type === "favorite"
        ? address.label
        : "");

    setValue(
      `arrets.${paletteIndex}.adresse`,
      {
        ...address.adresse,
        nom,
        isNumDetected:
          !!address.adresse.numero ||
          address.type === TypeAddressLabel[TypeAddress.POI] ||
          address.type === TypeAddressLabel[TypeAddress.Favorite],
      },
      { shouldValidate: true, shouldTouch: true },
    );
    setPaletteIndex(null);
  };

  const openFavoriteDrawer = (index: number) => {
    setFavoriteDrawerIndex(index);
    setFavoriteDrawerOpen(true);
  };

  const closeFavoriteDrawer = () => {
    setFavoriteDrawerOpen(false);
    setFavoriteDrawerIndex(null);
  };

  const saveCurrentAsFavorite = async (nom: string) => {
    if (favoriteDrawerIndex === null) return;

    const saved = await saveFavorite(
      nom,
      watch(`arrets.${favoriteDrawerIndex}.adresse`),
    );
    if (saved) closeFavoriteDrawer();
  };

  return {
    palette: {
      isOpen: paletteOpen,
      open: openAddressSearch,
      close: closeAddressSearch,
      select: selectAddress,
    },
    favoriteDrawer: {
      isOpen: favoriteDrawerOpen,
      index: favoriteDrawerIndex,
      open: openFavoriteDrawer,
      close: closeFavoriteDrawer,
      save: saveCurrentAsFavorite,
      isSaving,
    },
    favorites,
    isLoadingFavorites: isLoading,
  };
};
